import { useTaggedTime } from '@hooks/useTaggedTime'
import { useFetchEntriesByTimeCard } from '@hooks/useEntries'
import { createTotalByTagRenderObj } from '@utils/taggedTime'
import { secondsToTimestamp } from '@utils/time'
import { TooltipTable } from 'components/indicators/tooltip-table'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '@lib/tooltip'
import { Project } from './Project'
import { TaskItem } from './types'

type ProjectItem = { name: string; tasks: TaskItem[] }
type TasksByProject = Record<number, ProjectItem>

interface Props {
  timeCardId: number
}

export const HourAllocationTable: FC<Props> = ({ timeCardId }) => {
  const { t } = useTranslation()
  const entriesQuery = useFetchEntriesByTimeCard(timeCardId)
  const taggedTime = useTaggedTime(timeCardId)
  const entries = entriesQuery.data ?? []

  const taggedRenderObj = createTotalByTagRenderObj(taggedTime.data ?? [])

  const tasksByProject = entries.reduce<TasksByProject>((acc, entry) => {
    const projectId = entry.task.project.id

    const foundProjecItem = acc[projectId] ?? {
      name: entry.task.project.name,
      tasks: [],
    }

    const projectItem: ProjectItem = {
      ...foundProjecItem,
      tasks: [
        ...foundProjecItem.tasks,
        {
          label: entry.task.displayPath.join(' • '),
          name: entry.task.name,
          secondsLogged: entry.duration,
        },
      ],
    }

    return { ...acc, [projectId]: projectItem }
  }, {})

  const total = entries.reduce((sum, { duration }) => sum + duration, 0)
  const taggedTotal =
    taggedTime.data?.reduce((sum, time) => sum + time.duration, 0) ?? 0

  return (
    <>
      {Object.values(tasksByProject)
        .sort((p1, p2) => (p1.name > p2.name ? 1 : -1))
        .map((project) => (
          <Project
            key={project.name}
            name={project.name}
            tasks={project.tasks}
          />
        ))}
      <div className="flex flex-row items-center justify-between py-1 mt-2 text-sm text-neutral-900">
        <span className="font-semibold">{t('common.total')}</span>
        <div className="flex flex-col text-right">
          <span className="font-semibold">{secondsToTimestamp(total)}</span>
          {taggedTotal > 0 && (
            <>
              <span
                data-tooltip-id="tagged-total"
                className="text-xs font-normal text-orange-800"
              >
                {secondsToTimestamp(taggedTotal)}
              </span>
              <Tooltip
                id="tagged-total"
                place="bottom"
                delayShow={1000}
                closeOnEsc={true}
              >
                <TooltipTable data={taggedRenderObj} />
              </Tooltip>
            </>
          )}
        </div>
      </div>
    </>
  )
}
